/**
 * Expense Reports Dashboard View
 *
 * Team Assistant view for managing all expense reports.
 * Displays list of expense reports with filtering, sorting, bulk selection,
 * and bulk action capabilities.
 */

import { html, render } from 'lit-html';
import { apiClient, quickBooksBulkApi, fetchWithAuth } from '../services/api-client.js';
import { ApiError } from '../types/api.js';
import { getLinkRouter } from '../components/link.js';
import { authStore } from '../services/auth-store.js';
import { renderIcon } from '../utils/icons.js';
import { createSidebarLayout, updateSidebarBadge } from '../components/sidebar-layout.js';
import { bulkSelectionStore } from '../services/bulk-selection-store.js';
import { BulkProgressModal } from '../components/bulk-progress-modal.js';
import type { BulkOperationType } from '../components/bulk-progress-modal.js';
import type { RouteContext } from '../services/router.js';

// Type for expense report summary (simplified version since full types may not be available)
interface ExpenseReportSummary {
  id: string;
  user_id: string;
  expenditure_request_id: string;
  status: 'DRAFT' | 'SUBMITTED' | 'PAID' | 'CANCELLED';
  total_amount_chf: number | null;
  receipt_count?: number;
  created_at: string;
  submitted_at: string | null;
  request_title?: string;
  linked_request_title?: string;
  request_amount?: number;
  request_currency?: string;
  submitter_name?: string;
  submitter_email?: string;
}

/**
 * Expense report stats interface
 */
interface ExpenseReportStats {
  submitted: number;
  paid: number;
  total: number;
}

/**
 * Status color mappings
 */
const STATUS_COLORS = {
  SUBMITTED: { bg: 'bg-warning/20', text: 'text-warning' },
  PAID: { bg: 'bg-success/20', text: 'text-success' },
  CANCELLED: { bg: 'bg-muted', text: 'text-muted-foreground' },
  DRAFT: { bg: 'bg-muted', text: 'text-muted-foreground' },
} as const;

/**
 * Format date for display
 */
function formatDate(dateString: string | null): string {
  if (!dateString) return '-';

  const date = new Date(dateString);
  return new Intl.DateTimeFormat('en-CH', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

/**
 * Format currency amount
 */
function formatCurrency(amount: number | null, currency = 'CHF'): string {
  if (amount == null) return '-';

  return new Intl.NumberFormat('en-CH', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * Create expense reports dashboard view
 */
export function createExpenseReportsDashboardView() {
  let reports: ExpenseReportSummary[] = [];
  let stats: ExpenseReportStats = { submitted: 0, paid: 0, total: 0 };
  let statusFilter = 'SUBMITTED';
  let sortColumn = 'submitted_at';
  let sortOrder: 'asc' | 'desc' = 'desc';
  let loading = true;
  let error: string | null = null;
  let selectedIds: Set<string> = new Set();
  let unsubscribeSelection: (() => void) | null = null;
  let activeModal: BulkProgressModal | null = null;

  /**
   * Fetch expense reports from API
   */
  const fetchReports = async () => {
    loading = true;
    error = null;
    renderView();

    try {
      const params = new URLSearchParams({
        role: 'assistant',
      });

      if (statusFilter) {
        params.append('status', statusFilter);
      }
      params.append('sort', sortColumn);
      params.append('order', sortOrder);

      const response = await apiClient.get<{ data: ExpenseReportSummary[]; pagination?: unknown }>(`/expense-reports?${params}`);
      reports = response.data.data;

      // Update bulk selection store with current report IDs
      bulkSelectionStore.setAllIds(reports.map((r) => r.id));
    } catch (err: unknown) {
      // Handle 403 Forbidden - user doesn't have required role
      if (err && typeof err === 'object' && 'status' in err && err.status === 403) {
        getLinkRouter()?.navigate('/dashboard');
        return;
      }
      // Log other errors for debugging
      if (process.env.NODE_ENV !== 'production') {
        console.error('Failed to fetch expense reports:', err);
      }
      error = 'Failed to load expense reports. Please try again.';
    } finally {
      loading = false;
      renderView();
    }
  };

  let statsLoading = false;

  /**
   * Fetch statistics
   */
  const fetchStats = async () => {
    statsLoading = true;
    renderView();

    try {
      const response = await apiClient.get<{ data: ExpenseReportStats }>('/expense-reports/stats');
      stats = response.data.data;
      // Update sidebar badge with pending count
      updateSidebarBadge('pending-expense-reports', stats.submitted);
    } catch (err: unknown) {
      // Handle 403 silently - stats will show zeros
      if (err && typeof err === 'object' && 'status' in err && err.status === 403) {
        return;
      }
      if (process.env.NODE_ENV !== 'production') {
        console.error('Failed to fetch stats:', err);
      }
    } finally {
      statsLoading = false;
      renderView();
    }
  };

  /**
   * Handle column sort
   */
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      sortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      sortColumn = column;
      sortOrder = 'desc';
    }
    fetchReports();
  };

  /**
   * Handle status filter change
   */
  const handleFilterChange = (event: Event) => {
    const target = event.target as HTMLSelectElement;
    statusFilter = target.value;
    fetchReports();
  };

  /**
   * Handle row click (navigate to detail unless clicking checkbox)
   */
  const handleRowClick = (reportId: string, event: Event) => {
    const target = event.target as HTMLElement;
    // Don't navigate if clicking on the checkbox cell
    if (target.closest('.row-checkbox-cell')) return;
    getLinkRouter()?.navigate(`/expense-reports/${reportId}`);
  };

  /**
   * Handle row checkbox toggle
   */
  const handleCheckboxChange = (reportId: string, checked: boolean) => {
    if (checked) {
      bulkSelectionStore.selectItems([reportId]);
    } else {
      bulkSelectionStore.deselectItems([reportId]);
    }
  };

  /**
   * Handle select all checkbox toggle
   */
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      bulkSelectionStore.selectAll();
    } else {
      bulkSelectionStore.deselectAll();
    }
  };

  /**
   * Response type for bulk mark-paid API endpoint
   */
  interface BulkMarkPaidResult {
    id: string;
    success: boolean;
    error?: string;
  }

  interface BulkMarkPaidResponse {
    results: BulkMarkPaidResult[];
    summary: { total: number; succeeded: number; failed: number };
  }

  /**
   * Call the bulk mark-paid API endpoint and update modal progress per report
   */
  const runBulkMarkPaid = async (
    modal: BulkProgressModal,
    idsToProcess: string[]
  ): Promise<void> => {
    // Mark all as processing
    for (const reportId of idsToProcess) {
      modal.startItem(reportId);
    }

    try {
      const response = await apiClient.post<BulkMarkPaidResponse>(
        '/expense-reports/bulk/mark-paid',
        { reportIds: idsToProcess }
      );

      const data = response.data;

      // Update modal with individual results
      for (const result of data.results) {
        if (result.success) {
          modal.completeItem(result.id);
        } else {
          modal.failItem(result.id, result.error || 'Failed');
        }
      }
    } catch (err: unknown) {
      // If the whole request fails, mark all as failed
      const errorMessage = err instanceof Error ? err.message : 'Request failed';
      for (const reportId of idsToProcess) {
        modal.failItem(reportId, errorMessage);
      }
    }

    modal.showCompletion();
    bulkSelectionStore.deselectAll();
    // Refresh the list to reflect updated statuses
    await fetchReports();
    await fetchStats();
  };

  /**
   * Open a bulk progress modal for the given operation
   */
  const openBulkModal = (operationType: BulkOperationType) => {
    const selectedReportIds = Array.from(selectedIds);
    if (selectedReportIds.length === 0) return;

    const selectedReports = reports
      .filter((r) => selectedReportIds.includes(r.id))
      .map((r) => ({
        id: r.id,
        name: r.submitter_name
          ? `${r.submitter_name} - ${r.linked_request_title || r.request_title || r.id}`
          : r.linked_request_title || r.request_title || r.id,
      }));

    if (operationType === 'paid') {
      const modal = new BulkProgressModal(
        operationType,
        selectedReports,
        (failedIds) => {
          modal.setProcessing(true);
          runBulkMarkPaid(modal, failedIds).finally(() => {
            if (activeModal === modal) activeModal = null;
          });
        }
      );

      activeModal = modal;
      modal.open();
      modal.setProcessing(true);
      runBulkMarkPaid(modal, selectedReportIds).finally(() => {
        if (activeModal === modal) activeModal = null;
      });
      return;
    }

    const runOperation = async (idsToProcess: string[]) => {
      for (const reportId of idsToProcess) {
        modal.startItem(reportId);

        try {
          await performBulkAction(operationType, reportId);
          modal.completeItem(reportId);
        } catch (err: unknown) {
          const errorMessage =
            err instanceof Error ? err.message : 'An unexpected error occurred';
          modal.failItem(reportId, errorMessage);

          // Short-circuit on QB connection errors: no point calling the API
          // for every remaining report when the connection is known to be down.
          if (err instanceof ApiError && err.status === 503) {
            for (const remainingId of idsToProcess.slice(idsToProcess.indexOf(reportId) + 1)) {
              modal.startItem(remainingId);
              modal.failItem(remainingId, errorMessage);
            }
            break;
          }
        }
      }

      modal.showCompletion();
      // Clear selection after operation completes
      bulkSelectionStore.deselectAll();
    };

    const modal = new BulkProgressModal(
      operationType,
      selectedReports,
      (failedIds) => {
        modal.setProcessing(true);
        runOperation(failedIds);
      }
    );

    activeModal = modal;
    modal.open();
    modal.setProcessing(true);
    runOperation(selectedReportIds).finally(() => {
      if (activeModal === modal) {
        activeModal = null;
      }
    });
  };

  /**
   * Open bulk EPO generation modal.
   * Unlike other bulk operations, EPO uses a single batch API call that returns a ZIP file.
   * The modal shows all reports as "processing" while the ZIP is generated, then
   * marks them all as success/failed based on the downloaded file and shows a
   * download confirmation message.
   */
  const openBulkEpoModal = () => {
    const selectedReportIds = Array.from(selectedIds);
    if (selectedReportIds.length === 0) return;

    const selectedReports = reports
      .filter((r) => selectedReportIds.includes(r.id))
      .map((r) => ({
        id: r.id,
        name: r.submitter_name
          ? `${r.submitter_name} - ${r.linked_request_title || r.request_title || r.id}`
          : r.linked_request_title || r.request_title || r.id,
      }));

    const modal = new BulkProgressModal('epo', selectedReports);

    const runEpoGeneration = async () => {
      // Mark all reports as processing
      for (const report of selectedReports) {
        modal.startItem(report.id);
      }

      try {
        const dateStr = new Date().toISOString().split('T')[0];
        const zipFilename = `expense-payments-${dateStr}.zip`;

        // Make batch request to backend using fetchWithAuth to handle blob response
        const response = await fetchWithAuth('/api/expense-reports/bulk/generate-epo', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reportIds: selectedReportIds }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => null);
          throw new Error(errorData?.error || `Request failed (${response.status})`);
        }

        const blob = await response.blob();

        // Trigger browser download
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = zipFilename;
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        setTimeout(() => {
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
        }, 100);

        // Check for partial failures via response headers
        const failedCount = parseInt(response.headers.get('X-EPO-Failed-Count') || '0', 10);

        if (failedCount > 0) {
          // Some reports failed: show the last N as failed, rest as success
          // Since we cannot map individual failures from the blob response, we mark
          // all as succeeded but show a warning in the confirmation message that
          // _errors.json in the ZIP details which reports failed.
          for (const report of selectedReports) {
            modal.completeItem(report.id);
          }
          modal.showEpoDownloadConfirmation(`${zipFilename} (${failedCount} report(s) failed — see _errors.json in ZIP)`);
        } else {
          // All reports succeeded
          for (const report of selectedReports) {
            modal.completeItem(report.id);
          }
          modal.showEpoDownloadConfirmation(zipFilename);
        }
      } catch (err: unknown) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to generate EPO ZIP file';
        for (const report of selectedReports) {
          modal.failItem(report.id, errorMessage);
        }
        modal.showCompletion();
      }

      bulkSelectionStore.deselectAll();
    };

    activeModal = modal;
    modal.open();
    modal.setProcessing(true);
    runEpoGeneration().finally(() => {
      if (activeModal === modal) {
        activeModal = null;
      }
    });
  };

  /**
   * Perform the actual bulk action for a single report.
   * Story 3.2: QuickBooks sync implemented.
   */
  const performBulkAction = async (
    operationType: BulkOperationType,
    reportId: string
  ): Promise<void> => {
    if (operationType === 'quickbooks') {
      // Call bulk sync endpoint with a single report ID for per-report progress tracking (Story 3.2)
      // The API client throws ApiError on non-2xx responses, which the modal will catch and display.
      const response = await quickBooksBulkApi.bulkSyncToQuickBooks([reportId]);

      // Check the per-report result in the bulk response
      const result = response.data;
      if (result?.results && result.results.length > 0) {
        const reportResult = result.results[0];
        if (reportResult && !reportResult.success && !reportResult.skipped) {
          throw new Error(reportResult.error ?? 'Sync failed');
        }
        // Skipped (already synced) is treated as success in modal
      }
    } else {
      throw new Error(`${operationType} bulk action not yet implemented`);
    }
  };

  /**
   * Render the select-all checkbox state
   */
  const renderSelectAllCheckbox = () => {
    const isAll = bulkSelectionStore.isAllSelected();
    const isSome = bulkSelectionStore.isSomeSelected();

    return html`
      <div class="row-checkbox-cell flex items-center justify-center">
        <label class="inline-flex items-center cursor-pointer">
          <span class="sr-only">Select all expense reports</span>
          <input
            type="checkbox"
            class="h-4 w-4 rounded border border-neutral-800 bg-neutral-950/60 backdrop-blur-sm appearance-none cursor-pointer transition-colors
                   checked:bg-primary checked:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 focus:ring-offset-2 focus:ring-offset-background"
            .checked=${isAll}
            .indeterminate=${isSome && !isAll}
            @change=${(e: Event) => handleSelectAll((e.target as HTMLInputElement).checked)}
            aria-label="Select all expense reports"
          />
        </label>
      </div>
    `;
  };

  /**
   * Render a row checkbox for a specific report
   */
  const renderRowCheckbox = (reportId: string) => {
    const isChecked = selectedIds.has(reportId);

    return html`
      <div class="row-checkbox-cell flex items-center justify-center" @click=${(e: Event) => e.stopPropagation()}>
        <label class="inline-flex items-center cursor-pointer">
          <span class="sr-only">Select expense report</span>
          <input
            type="checkbox"
            class="h-4 w-4 rounded border border-neutral-800 bg-neutral-950/60 backdrop-blur-sm appearance-none cursor-pointer transition-colors
                   checked:bg-primary checked:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 focus:ring-offset-2 focus:ring-offset-background"
            .checked=${isChecked}
            @change=${(e: Event) => handleCheckboxChange(reportId, (e.target as HTMLInputElement).checked)}
            aria-label="Select expense report"
          />
        </label>
      </div>
    `;
  };

  /**
   * Render the bulk action toolbar (shown when items are selected)
   * Always rendered for CSS transition animation; visibility toggled via opacity/translate.
   */
  const renderBulkToolbar = () => {
    const count = selectedIds.size;
    const visible = count > 0;

    return html`
      <div
        class="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 transition-all duration-300 ${visible ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 translate-y-4 pointer-events-none'}"
        aria-live="polite"
        aria-label="${count} ${count === 1 ? 'report' : 'reports'} selected"
      >
        <div class="flex items-center gap-3 px-4 py-3 bg-card/80 border border-border rounded-lg shadow-lg backdrop-blur-sm">
          <div class="flex items-center gap-2">
            <span class="text-sm font-medium text-foreground">${count} ${count === 1 ? 'report' : 'reports'} selected</span>
            <button
              type="button"
              class="text-sm text-muted-foreground hover:text-foreground transition-colors"
              @click=${() => bulkSelectionStore.deselectAll()}
            >
              Clear
            </button>
          </div>
          <div class="w-px h-6 bg-border"></div>
          <div class="flex items-center gap-2">
            <button
              type="button"
              class="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/80 transition-colors"
              @click=${() => openBulkModal('quickbooks')}
              aria-label="Book selected reports in QuickBooks"
            >
              ${renderIcon('creditCard', { size: 'sm' })}
              Book in QuickBooks
            </button>
            <button
              type="button"
              class="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/80 transition-colors"
              @click=${() => openBulkEpoModal()}
              aria-label="Generate EPO for selected reports"
            >
              ${renderIcon('fileText', { size: 'sm' })}
              Generate EPO
            </button>
            <button
              type="button"
              class="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/80 transition-colors"
              @click=${() => openBulkModal('paid')}
              aria-label="Mark selected reports as paid"
            >
              ${renderIcon('checkCircle', { size: 'sm' })}
              Mark as Paid
            </button>
          </div>
        </div>
      </div>
    `;
  };

  /**
   * Render sort indicator
   */
  const renderSortIndicator = (column: string) => {
    if (sortColumn !== column) return html``;

    return html`
      <span class="ml-1 inline-flex">
        ${sortOrder === 'asc'
          ? renderIcon('arrowUp', { size: 'sm' })
          : renderIcon('arrowDown', { size: 'sm' })}
      </span>
    `;
  };

  /**
   * Main template
   */
  const template = () => html`
    <div class="expense-reports-dashboard" data-testid="expense-reports-list">
      <!-- Header -->
      <div class="flex items-center justify-between mb-6">
        <div class="flex items-center gap-4">
          <h1 class="text-2xl font-semibold text-foreground">Expense Reports</h1>
          ${statsLoading
            ? html`<span class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-muted/20 text-muted-foreground animate-pulse">Loading...</span>`
            : html`<span class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-warning/20 text-warning">${stats.submitted} pending</span>`}
        </div>
      </div>

      <!-- Filters -->
      <div class="glass-sm rounded-xl p-4 mb-6">
        <div class="flex items-center gap-4">
          <label class="flex items-center gap-2">
            <span class="text-sm font-medium text-muted-foreground">Status:</span>
            <select
              class="px-3 py-1.5 rounded-lg bg-background/50 border border-border/50 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
              @change=${handleFilterChange}
            >
              <option value="">All</option>
              <option value="SUBMITTED" selected>Submitted</option>
              <option value="PAID">Paid</option>
              <option value="CANCELLED">Cancelled</option>
            </select>
          </label>
        </div>
      </div>

      <!-- Table -->
      <div class="glass-sm rounded-xl overflow-hidden">
        ${loading
          ? html`
              <div class="flex items-center justify-center py-12">
                <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            `
          : error
          ? html`
              <div class="p-6 text-center">
                <p class="text-destructive">${error}</p>
              </div>
            `
          : reports.length === 0
          ? html`
              <div class="p-12 text-center">
                <div class="mb-4">${renderIcon('fileX', { size: 'lg', className: 'text-muted-foreground' })}</div>
                <p class="text-muted-foreground">No expense reports found</p>
              </div>
            `
          : html`
              <table class="w-full">
                <thead>
                  <tr class="border-b border-border/50 bg-muted/20">
                    <th class="px-4 py-3 w-10" aria-label="Select all">
                      ${renderSelectAllCheckbox()}
                    </th>
                    <th
                      class="px-4 py-3 text-left text-sm font-medium text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
                      @click=${() => handleSort('submitter_name')}
                    >
                      Submitter ${renderSortIndicator('submitter_name')}
                    </th>
                    <th
                      class="px-4 py-3 text-left text-sm font-medium text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
                      @click=${() => handleSort('submitted_at')}
                    >
                      Submission Date ${renderSortIndicator('submitted_at')}
                    </th>
                    <th
                      class="px-4 py-3 text-right text-sm font-medium text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
                      @click=${() => handleSort('total_amount_chf')}
                    >
                      Amount (CHF) ${renderSortIndicator('total_amount_chf')}
                    </th>
                    <th class="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                      Linked Request
                    </th>
                    <th class="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  ${reports.map(
                    (report) => html`
                      <tr
                        class="border-b border-border/30 hover:bg-muted/10 cursor-pointer transition-colors ${selectedIds.has(report.id) ? 'bg-primary/5' : ''}"
                        data-testid="expense-report-row"
                        @click=${(e: Event) => handleRowClick(report.id, e)}
                      >
                        <td class="px-4 py-3 w-10">
                          ${renderRowCheckbox(report.id)}
                        </td>
                        <td class="px-4 py-3 text-sm text-foreground">
                          ${report.submitter_name || '-'}
                        </td>
                        <td class="px-4 py-3 text-sm text-muted-foreground">
                          ${formatDate(report.submitted_at)}
                        </td>
                        <td class="px-4 py-3 text-sm text-foreground text-right font-medium">
                          ${formatCurrency(report.total_amount_chf)}
                        </td>
                        <td class="px-4 py-3 text-sm text-foreground">
                          ${report.linked_request_title || '-'}
                        </td>
                        <td class="px-4 py-3">
                          <span
                            class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[
                              report.status
                            ]?.bg || 'bg-muted'} ${STATUS_COLORS[report.status]?.text ||
                            'text-muted-foreground'}"
                          >
                            ${report.status}
                          </span>
                        </td>
                      </tr>
                    `
                  )}
                </tbody>
              </table>
            `}
      </div>

      <!-- Bulk Action Toolbar (shown when items are selected) -->
      ${renderBulkToolbar()}
    </div>
  `;

  /**
   * Render the view
   */
  const renderView = () => {
    if (!container) return;

    // Create a content element for the template
    const content = document.createElement('div');
    render(template(), content);

    // Clear container and append sidebar layout with content
    container.innerHTML = '';
    container.appendChild(createSidebarLayout(content, {
      currentPath: '/expense-reports-dashboard',
      title: 'Expense Reports Dashboard',
    }));
  };

  let container: HTMLElement | null = null;

  return {
    /**
     * Initialize and render the view
     */
    async render(target: HTMLElement) {
      container = target;

      // Check authentication
      const state = authStore.getState();
      if (!state.isAuthenticated) {
        getLinkRouter()?.navigate('/login');
        return;
      }

      // Check role
      if (state.user?.role !== 'ASSISTANT' && state.user?.role !== 'ADMIN') {
        getLinkRouter()?.navigate('/dashboard');
        return;
      }

      // Subscribe to selection changes to trigger re-render
      unsubscribeSelection = bulkSelectionStore.subscribe((ids) => {
        selectedIds = ids;
        renderView();
      });

      renderView();

      // Fetch data in parallel
      await Promise.all([fetchReports(), fetchStats()]);
    },

    /**
     * Clean up when view is destroyed
     */
    destroy() {
      container = null;
      // Close any open bulk modal to prevent scroll lock and dangling async operations
      if (activeModal) {
        activeModal.forceClose();
        activeModal = null;
      }
      // Clean up selection subscription and reset store on navigation away
      if (unsubscribeSelection) {
        unsubscribeSelection();
        unsubscribeSelection = null;
      }
      bulkSelectionStore.reset();
    },
  };
}

/**
 * Router wrapper for expense reports dashboard view
 */
export function expenseReportsDashboardView(_context?: RouteContext): HTMLElement {
  const container = document.createElement('div');
  container.className = 'px-4 lg:px-6 py-6';
  const view = createExpenseReportsDashboardView();
  view.render(container);
  return container;
}